import { useEffect, type ReactNode } from "react";
import { DevLoginPanel, useDevLoginPersonas } from "@dolfin/ui";
import { useAuth } from "./AuthProvider";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) return <SignIn />;

  return <>{children}</>;
}

// Straight to Google, unless local dev login is on: then offer it beside Google.
function SignIn() {
  const personas = useDevLoginPersonas("/api/auth/dev-login/personas");

  useEffect(() => {
    if (personas === null || personas?.length === 0) window.location.href = "/api/auth/login";
  }, [personas]);

  if (!personas?.length) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const signIn = async (persona: { key: string }) => {
    const resp = await fetch("/api/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personaKey: persona.key }),
    });
    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      return problem?.detail ?? "Dev login failed.";
    }
    window.location.reload();
    return null;
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Dolfin Admin</CardTitle>
          <CardDescription>Sign in with your dolfinai.co account</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <Button variant="outline" size="lg" className="w-full" onClick={() => (window.location.href = "/api/auth/login")}>
            Continue with Google
          </Button>
          <DevLoginPanel personas={personas} onSignIn={signIn} />
        </CardContent>
      </Card>
    </div>
  );
}
